/**
 * useDeckungsbeitrag (#1781) — the single twin-API interface point for
 * the Deckungsbeitrag app (APPS_LAYER convention: only the hook touches
 * twinService). Reads the ``deckungsbeitrag`` derived facts (one per
 * (crop_cycle, period), incl. #1720 establishment-only thin facts) and
 * display-joins them with the ``crop_cycle`` + ``field`` entities. The
 * fiscal-year start for WJ labelling rides the retained ``liquiditaet_config``
 * fact (default Juli), exactly like useLiquiditaetsplanung. (#2354 retired the
 * Betriebszweige view, so the page no longer fetches ``cost_center`` names.)
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  twinService,
  type DerivedFactListResponse,
  type EntityListResponse,
} from '../services/twinService';
import { fetchAllPages } from '../services/paging';
import {
  toCropCycleInfo,
  toDeckungsbeitragRow,
  withCropCycle,
  withHauptschlag,
} from '../pages/apps/deckungsbeitrag/deckungsbeitragMappers';
import {
  dedupeByFactId,
  withoutFutureWirtschaftsjahre,
} from '../pages/apps/deckungsbeitrag/deckungsbeitragRollup';
import { isMarginBasis, type MarginBasis } from '../pages/apps/deckungsbeitrag/basis';
import type { DeckungsbeitragRow } from '../pages/apps/deckungsbeitrag/types';

// The backend caps `limit` at 1000 per page. Facts scale one-per-(crop_cycle,
// period) — low hundreds on a pilot farm — but fetch ALL pages anyway (silently
// truncating at one page corrupts totals + rankings). MAX_PAGES is a runaway
// guard (10×1000 rows).
const PAGE_LIMIT = 1000;
const MAX_PAGES = 10;

export const deckungsbeitragKeys = {
  all: ['deckungsbeitrag'] as const,
  facts: (factType: string) => [...deckungsbeitragKeys.all, factType] as const,
  entities: (entityType: string) => [...deckungsbeitragKeys.all, 'entity', entityType] as const,
};

async function fetchAllFacts(factType: string): Promise<DerivedFactListResponse> {
  const all = await fetchAllPages(
    async (page) => {
      const res = await twinService.queryDerivedFacts({ fact_type: factType, page, limit: PAGE_LIMIT });
      return { items: res.derived_facts ?? [], hasMore: res.has_more };
    },
    { pageCap: MAX_PAGES, onCapExceeded: 'truncate' },
  );
  return { derived_facts: all, total: all.length, page: 1, limit: all.length, has_more: false };
}

function useFactQuery(factType: string) {
  return useQuery({
    queryKey: deckungsbeitragKeys.facts(factType),
    queryFn: () => fetchAllFacts(factType),
    staleTime: 30_000,
  });
}

async function fetchAllEntities(entityType: string): Promise<Record<string, unknown>[]> {
  return fetchAllPages(
    async (page) => {
      const res: EntityListResponse = await twinService.queryEntities(entityType, { page, limit: PAGE_LIMIT });
      return { items: res.entities ?? [], hasMore: res.has_more ?? false };
    },
    { pageCap: MAX_PAGES, onCapExceeded: 'truncate' },
  );
}

function useEntityQuery(entityType: string) {
  return useQuery({
    queryKey: deckungsbeitragKeys.entities(entityType),
    queryFn: () => fetchAllEntities(entityType),
    staleTime: 30_000,
  });
}

export interface UseDeckungsbeitragResult {
  /** Deduped + crop_cycle-joined rows, unsorted (ranking is the view's concern). */
  rows: DeckungsbeitragRow[];
  /** Fiscal-year start month off the liquiditaet_config fact; default 7 (Juli). */
  wjStartMonth: number;
  /**
   * The per-tenant default Bezugsgröße (#2304). Read from the OPTIONAL
   * ``deckungsbeitrag_default_basis`` key on the same retained config fact (the
   * ``wj_start_month`` read precedent); ``'ha'`` when absent — the key is unset
   * for every tenant today and is set operationally (admin-mcp) only when a
   * tenant wants a different default. #2303 deferred the carrier, so #2304 owns
   * the read with this safe fallback (no producer required).
   */
  defaultBasis: MarginBasis;
  /** field_id → Schlag name (nameless fields omitted — the label falls back to crop/variety). */
  fieldNames: Record<string, string>;
  /**
   * HOF-195 — the tenant's curated Hauptschlag analysis units: the ``hauptschlag`` strings
   * whose blocks the Deckungsbeitrag tree unites into one row. Read from the OPTIONAL
   * ``deckungsbeitrag_analysis_units`` key on the same retained config fact
   * ``deckungsbeitrag_default_basis`` (#2304) rides — same channel, same admin-mcp write
   * path, no producer required, and **empty when absent, which is every tenant until one
   * is curated**. Absent ⇒ the page renders exactly as it did before this issue.
   *
   * Deliberately NOT the raw ``field.hauptschlag`` value: on WF today that string spans
   * four separate register fields of one Kultur and Sorte under ``2- Autobahn Tunnel``,
   * which Max never asked to unite — grouping on it would invent the answer to the
   * general block-to-unit mapping he still owes by email.
   */
  analysisUnits: string[];
  isLoading: boolean;
  error: Error | null;
}

export function useDeckungsbeitrag(): UseDeckungsbeitragResult {
  const factQuery = useFactQuery('deckungsbeitrag');
  // The WJ start month has no account home (a planning preference, §4.5 of the
  // finance ontology) — read-of-record stays the retained config fact.
  const configQuery = useFactQuery('liquiditaet_config');
  const cycleQuery = useEntityQuery('crop_cycle');
  const fieldQuery = useEntityQuery('field');

  const wjStartMonth = useMemo(() => {
    const fv = configQuery.data?.derived_facts?.[0]?.fact_value;
    const raw = fv && typeof fv === 'object' ? (fv as Record<string, unknown>).wj_start_month : undefined;
    const n = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
    return Number.isFinite(n) && n >= 1 && n <= 12 ? n : 7;
  }, [configQuery.data]);

  const defaultBasis = useMemo<MarginBasis>(() => {
    const fv = configQuery.data?.derived_facts?.[0]?.fact_value;
    const raw =
      fv && typeof fv === 'object' ? (fv as Record<string, unknown>).deckungsbeitrag_default_basis : undefined;
    return isMarginBasis(raw) ? raw : 'ha';
  }, [configQuery.data]);

  // HOF-195 — field_id → Hauptschlag, off the SAME already-fetched ``field`` rows that
  // build ``fieldNames`` below. Fields without the attribute are omitted, so an id that
  // resolves to no entry and an entity without a Hauptschlag both join as null.
  const fieldHauptschlag = useMemo(() => {
    const byId: Record<string, string> = {};
    for (const entity of fieldQuery.data ?? []) {
      const id =
        (typeof entity.field_id === 'string' && entity.field_id) ||
        (typeof entity.entity_id === 'string' ? entity.entity_id : '');
      const hauptschlag = typeof entity.hauptschlag === 'string' ? entity.hauptschlag : '';
      if (id && hauptschlag) byId[id] = hauptschlag;
    }
    return byId;
  }, [fieldQuery.data]);

  const analysisUnits = useMemo<string[]>(() => {
    const fv = configQuery.data?.derived_facts?.[0]?.fact_value;
    const raw =
      fv && typeof fv === 'object' ? (fv as Record<string, unknown>).deckungsbeitrag_analysis_units : undefined;
    // Defensive to the shape, like every other read off this fact: a non-array, or an
    // array carrying anything but non-empty strings, degrades to "no units curated" —
    // which is the status quo, never a half-applied grouping.
    if (!Array.isArray(raw)) return [];
    return [...new Set(raw.filter((v): v is string => typeof v === 'string' && v !== ''))];
  }, [configQuery.data]);

  const rows = useMemo(() => {
    const cyclesById = new Map(
      (cycleQuery.data ?? []).map((e) => {
        const info = toCropCycleInfo(e);
        return [info.crop_cycle_id, info] as const;
      }),
    );
    const mapped = (factQuery.data?.derived_facts ?? []).map(toDeckungsbeitragRow);
    // HOF-195 — the Hauptschlag join runs AFTER ``withCropCycle``: a fact need not carry
    // ``field_id`` itself, the cycle supplies it, so joining first would miss every row
    // whose Schlag comes from its crop_cycle.
    const joined = dedupeByFactId(mapped)
      .map((row) => withCropCycle(row, cyclesById.get(row.crop_cycle_id)))
      .map((row) => withHauptschlag(row, fieldHauptschlag));
    // #2268: hide future Wirtschaftsjahre (crop_year > current WJ) once, HERE, so
    // every consumer — period tabs, rowsForPeriod, periodLensItems, summary, chart,
    // rollup — is consistent and a ?period=<future-year> deep-link yields nothing.
    // Keeps the partial-current WJ + the null (OHNE_JAHR) bucket.
    return withoutFutureWirtschaftsjahre(joined, wjStartMonth);
  }, [factQuery.data, cycleQuery.data, fieldHauptschlag, wjStartMonth]);

  const fieldNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (const entity of fieldQuery.data ?? []) {
      // Flat entity rows expose the pk as ``entity_id``; the attributes JSON
      // does NOT repeat ``field_id`` (unlike crop_cycle rows).
      const id =
        (typeof entity.field_id === 'string' && entity.field_id) ||
        (typeof entity.entity_id === 'string' ? entity.entity_id : '');
      const name = typeof entity.name === 'string' ? entity.name : '';
      if (id && name) names[id] = name;
    }
    return names;
  }, [fieldQuery.data]);

  const queries = [factQuery, configQuery, cycleQuery, fieldQuery];
  const isLoading = queries.some((q) => q.isLoading);
  const error = (queries.find((q) => q.error)?.error as Error | undefined) ?? null;

  return { rows, wjStartMonth, defaultBasis, analysisUnits, fieldNames, isLoading, error };
}

/**
 * One ``operation.labour`` entry as served (attrs promoted to top-level by
 * ``_flatten_entity_row``). The FE ports the engine's ``_labour_cost`` derivation
 * (#2442 source breakdown): an explicit non-zero ``cost`` wins, else ``hours×rate``,
 * else ``akkord_rate×kg``. All fields optional/defensive.
 */
export interface LabourEntry {
  cost?: number | null;
  hours?: number | null;
  rate?: number | null;
  akkord_rate?: number | null;
  kg?: number | null;
}

/** One ``operation.machinery`` entry (``{machine, hours, rate|cost}`` or a bare number). */
export interface MachineryEntry {
  machine?: string | null;
  cost?: number | null;
  hours?: number | null;
  rate?: number | null;
  /**
   * HOF-307 — the AREA the pass worked, off the journal's ``Arbeiten / Maschinen:`` section.
   * The twin has carried it since that issue; this shape had not declared it.
   */
  ha?: number | null;
  /**
   * HOF-519 — the unit ``rate`` is quoted per (``ha`` / ``h`` / ``St``). Load-bearing rather
   * than decorative: the ingest lane derives ``cost`` from ``ha × rate`` only where the rate is
   * per AREA, so a rate WITHOUT a cost beside it is not a bug — it is an hourly (or per-piece)
   * machine on a pass that recorded no hours. Its € comes from the Maschinen cascade on the
   * fact (``maschinen_posten``), never from re-multiplying these two here.
   */
  rate_unit?: string | null;
}

/** One pick/booking off the high-volume ``operation`` entity, narrowed for display. */
/**
 * HOF-307 — one material input of a fertilize / spray operation as the journal booked it:
 * the article, its Menge and unit, and the € the ingest priced it at (null when the
 * year's price list did not quote the article). The valued € per input lives on the FACT
 * (``duenger_posten`` / ``psm_posten``), keyed back to the operation by id.
 */
export interface OperationInput {
  article: string;
  menge: number | null;
  unit: string | null;
  cost: number | null;
}

export interface OperationInfo {
  operation_id: string;
  type: string;
  date: string | null;
  qty: number | null;
  unit: string | null;
  /** HOF-307 — the operation's material inputs; empty when it carries none. */
  inputs: OperationInput[];
  // HOF-104 — ``revenue`` and ``channel`` are GONE from this shape, in lockstep with
  // ``deckungsbeitrag_reads._operation_info``, which stopped serving them. They were the
  // wiberry allocator's kg-weighted booth share and the crop's dominant channel; HOF-102
  // made the Leistung ``Menge x Preisspiegel`` and the share stopped being a revenue
  // input, so the producer was retired. Nothing on this page read ``revenue``
  // (``opContribution`` reads it on no bucket since HOF-102); ``channel`` fed the row
  // detail's „· Vermarktung: …" clause, which goes with it.
  /**
   * #2442 — the material cost € of the operation (buckets to Dünger/PSM/Saatgut/
   * Sonstige by ``type``/``ledger_category``). Null/0 when the op carries no material cost.
   */
  cost: number | null;
  /** #2442 — raw ``labour`` entries; the source breakdown derives per-op Löhne € from them. */
  labour: LabourEntry[];
  /** #2442 — raw ``machinery`` entries; the source breakdown derives per-op Maschinen €. */
  machinery: MachineryEntry[];
  /** #2442 — explicit GuV ledger override; when it buckets to a direct cost it wins over ``type``. */
  ledger_category: string | null;
  /**
   * #2769-a — the DISPLAY-ONLY share of an undated MyFarm24 Jahreskosten lump that falls on
   * this dated Maßnahme. It is deliberately NOT money to the engine: the worker's
   * ``_operation_lines`` sums ``cost`` and never this, so the lump keeps every cent and the
   * bucket total is byte-identical with and without it. The drill-down uses it for date
   * grain — WHEN the lump's money was spent — and must never add it to a bucket.
   */
  apportioned_cost: number | null;
  /**
   * #2769-a — the ORIGIN of ``apportioned_cost``: the ``operation_id`` of the undated lump the
   * share was computed from. Required for the row to render at all: the breakdown deducts the
   * share from that lump's own line, so a share whose origin it cannot resolve is dropped
   * rather than added (which would inflate the bucket by exactly the allocation).
   */
  apportioned_from: string | null;
}

const opStrOrNull = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const opNumOrNull = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};

/** Coerce a served ``labour``/``machinery`` attribute (array, single object, or absent) to a list. */
const toEntryList = <T>(v: unknown, map: (o: Record<string, unknown>) => T): T[] => {
  const items = Array.isArray(v) ? v : v && typeof v === 'object' ? [v] : [];
  return items.filter((e): e is Record<string, unknown> => !!e && typeof e === 'object' && !Array.isArray(e)).map(map);
};

const toLabourEntry = (o: Record<string, unknown>): LabourEntry => ({
  cost: opNumOrNull(o.cost),
  hours: opNumOrNull(o.hours),
  rate: opNumOrNull(o.rate),
  akkord_rate: opNumOrNull(o.akkord_rate),
  kg: opNumOrNull(o.kg),
});

const toMachineryEntry = (o: Record<string, unknown>): MachineryEntry => ({
  machine: opStrOrNull(o.machine),
  cost: opNumOrNull(o.cost),
  hours: opNumOrNull(o.hours),
  rate: opNumOrNull(o.rate),
  ha: opNumOrNull(o.ha),
  rate_unit: opStrOrNull(o.rate_unit),
});

const toOperationInput = (o: Record<string, unknown>): OperationInput => ({
  article: opStrOrNull(o.article) ?? '',
  menge: opNumOrNull(o.menge),
  unit: opStrOrNull(o.unit),
  cost: opNumOrNull(o.cost),
});

export const toOperationInfo = (entity: Record<string, unknown>): OperationInfo => {
  return {
    operation_id:
      (typeof entity.operation_id === 'string' && entity.operation_id) ||
      (typeof entity.entity_id === 'string' ? entity.entity_id : ''),
    type: typeof entity.type === 'string' ? entity.type : '',
    date: opStrOrNull(entity.date),
    qty: opNumOrNull(entity.qty),
    unit: opStrOrNull(entity.unit),
    // HOF-307 — an input without an article is not an input (nothing could name or price it).
    inputs: toEntryList(entity.inputs, toOperationInput).filter((i) => i.article !== ''),
    cost: opNumOrNull(entity.cost),
    labour: toEntryList(entity.labour, toLabourEntry),
    machinery: toEntryList(entity.machinery, toMachineryEntry),
    ledger_category: opStrOrNull(entity.ledger_category),
    apportioned_cost: opNumOrNull(entity.apportioned_cost),
    apportioned_from: opStrOrNull(entity.apportioned_from),
  };
};

/**
 * Lazy per-crop_cycle operations (v2 row detail). ``operation`` is a
 * high-volume entity (deliberately excluded from embeddings), so the page
 * never fetches it up front — only the expanded row's cycle, server-side
 * filtered via ``attr_in``. Disabled while no row is expanded.
 */
export function useCropCycleOperations(cropCycleId: string | null): {
  operations: OperationInfo[];
  isLoading: boolean;
} {
  const query = useQuery({
    queryKey: [...deckungsbeitragKeys.all, 'operations', cropCycleId],
    enabled: cropCycleId != null,
    staleTime: 30_000,
    queryFn: async () => {
      const all = await fetchAllPages(
        async (page) => {
          const res: EntityListResponse = await twinService.queryEntities('operation', {
            attr_in: { crop_cycle_id: [cropCycleId as string] },
            page,
            limit: PAGE_LIMIT,
          });
          return { items: res.entities ?? [], hasMore: res.has_more ?? false };
        },
        { pageCap: MAX_PAGES, onCapExceeded: 'truncate' },
      );
      return all.map(toOperationInfo);
    },
  });
  return { operations: query.data ?? [], isLoading: query.isLoading };
}
